import type { CueCategory } from '@ohear/shared'
import { copy } from '@ohear/shared'
/**
 * Hand-rolled recording state machine.
 *
 * Transitions (PRD §10):
 *   IDLE        → PRELOAD     when actor lands on a cue
 *   PRELOAD     → IDLE        when assets ready
 *   IDLE        → THREE_POP   on Enter (start recording flow)
 *   THREE_POP   → RECORDING   at t=cue_in (after 3-pop beeps)
 *   RECORDING   → QC_LOCAL    when audio stop fires (cue_out + 3s post)
 *   QC_LOCAL    → UPLOAD      if local QC passes
 *   UPLOAD      → QC_SERVER   when upload complete
 *   QC_SERVER   → REVIEW      when server STT done
 *   REVIEW      → ADVANCE     on N
 *   REVIEW      → THREE_POP   on R (Pickup, version v++)
 *
 * The machine doesn't own the Recorder — it calls into a passed-in {@link UseRecorderApi}
 * for the audio side. Same for upload, presign, finalize, all injected as
 * callbacks. This keeps the machine testable and lets the host swap mock impls.
 */
import { useCallback, useEffect, useMemo, useRef } from 'react'
import {
  type Phase,
  type TakeRecord,
  nextPickupVersion,
  nextTakeNumber,
  useRecordingStore,
} from './state'
import type { UseRecorderApi } from './useRecorder'

/**
 * 3-pop pre-roll length in milliseconds (2 s preroll + 3 × 1 s beeps), matches
 * StreamerController's CUE_IN_MS = 5000.
 */
const THREE_POP_MS = 5000

/**
 * Post-roll milliseconds after cue_out — keeps the room tone tail so server
 * QC can measure noise floor on the trailing silence.
 */
const POST_ROLL_MS = 3000

export interface MachineCue {
  cueId: string
  /** Cue duration (cue_out − cue_in) in seconds. */
  durationSec: number
  /** Predictive category for predictive hint banner + reset reminder. */
  category: CueCategory
}

export interface MachineCallbacks {
  /** Mint a presigned URL + server take id. */
  presign: (input: {
    cueId: string
    takeNumber: number
    versionNumber: number
    durationSec: number
  }) => Promise<{ takeId: string; uploadUrl: string }>
  /** Upload the WAV body to the URL; resolve with sha256 of payload. */
  upload: (input: { takeId: string; uploadUrl: string; pcm: Float32Array }) => Promise<string>
  /** Finalize the take server-side (kicks off QC + STT). */
  finalize: (input: { takeId: string; cueId: string }) => Promise<void>
}

export interface UseRecordingMachineApi {
  phase: Phase
  /** Begin recording flow (Enter key). */
  beginRecord: () => Promise<void>
  /** Re-record current cue, bump version (Pickup / R key). */
  pickup: () => Promise<void>
  /** Skip to next cue without recording (N key during review). */
  advance: () => void
  /** Abort whatever is in flight (Esc key). */
  abort: () => void
}

interface UseRecordingMachineProps {
  recorder: UseRecorderApi
  callbacks: MachineCallbacks
  /** Currently-selected cue. Null = nothing armed. */
  activeCue: MachineCue | null
  /** Previous cue category — drives reset reminder when transitioning. */
  previousCategory: CueCategory | null
  /** Called with the next cue id after a successful advance. */
  onAdvance: () => void
}

export function useRecordingMachine(props: UseRecordingMachineProps): UseRecordingMachineApi {
  const { recorder, callbacks, activeCue, previousCategory, onAdvance } = props
  const phase = useRecordingStore((s) => s.phase)
  const takesByCue = useRecordingStore((s) => s.takesByCue)
  const setPhase = useRecordingStore((s) => s.setPhase)
  const pushTake = useRecordingStore((s) => s.pushTake)
  const updateTakeUpload = useRecordingStore((s) => s.updateTakeUpload)
  const setError = useRecordingStore((s) => s.setError)
  const setResetReminder = useRecordingStore((s) => s.setResetReminder)

  /** Track the in-flight take across the THREE_POP→RECORDING→QC sequence. */
  const inFlightRef = useRef<{
    cueId: string
    takeNumber: number
    versionNumber: number
    threePopTimer: ReturnType<typeof setTimeout> | null
    autoStopTimer: ReturnType<typeof setTimeout> | null
  } | null>(null)

  /** Common path: actually begin THREE_POP → RECORDING for a cue. */
  const arm = useCallback(
    async (cue: MachineCue, takeNumber: number, versionNumber: number) => {
      try {
        setPhase('three_pop')
        // Start the recorder right at THREE_POP so the room tone preceding
        // cue_in is captured. The first 5s of audio is pre-roll and will
        // be discarded server-side via cue_in marker.
        await recorder.start()
        inFlightRef.current = {
          cueId: cue.cueId,
          takeNumber,
          versionNumber,
          threePopTimer: setTimeout(() => {
            setPhase('recording')
            // Auto-stop at cue_out + post-roll.
            const stopAfterMs = cue.durationSec * 1000 + POST_ROLL_MS
            if (inFlightRef.current) {
              inFlightRef.current.autoStopTimer = setTimeout(() => {
                void completeTake().catch((e) => {
                  setError(formatError(e))
                  setPhase('idle')
                })
              }, stopAfterMs)
            }
          }, THREE_POP_MS),
          autoStopTimer: null,
        }
      } catch (err) {
        setError(formatError(err))
        setPhase('idle')
      }
    },
    // completeTake is defined below in the same closure scope
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [recorder, setError, setPhase],
  )

  /** Stop the recorder, run local QC, upload, finalize. */
  const completeTake = useCallback(async () => {
    const inFlight = inFlightRef.current
    if (!inFlight) return
    if (inFlight.threePopTimer) clearTimeout(inFlight.threePopTimer)
    if (inFlight.autoStopTimer) clearTimeout(inFlight.autoStopTimer)

    setPhase('qc_local')
    let result
    try {
      result = await recorder.stop()
    } catch (err) {
      inFlightRef.current = null
      setError(formatError(err))
      setPhase('idle')
      return
    }

    // Local QC stub — peak-only check. The full QC engine in @ohear/audio
    // runs server-side; here we just guard against truly broken takes.
    if (result.peak < 0.001) {
      setError('No audio detected — check your microphone is selected and unmuted.')
      inFlightRef.current = null
      setPhase('idle')
      return
    }
    if (result.peak >= 1.0) {
      // Hard clip — let the actor decide via server QC, but still flag locally.
      const clipMsg = copy.qc.QC.clipping_fail
      setError(clipMsg ? clipMsg.en : 'Audio clipped — try lowering input gain.')
    }

    // Stash the take in client state immediately so TakeHistory updates.
    const localTake: TakeRecord = {
      takeId: makeClientTakeId(),
      cueId: inFlight.cueId,
      takeNumber: inFlight.takeNumber,
      versionNumber: inFlight.versionNumber,
      recordedAt: Date.now(),
      durationSec: result.durationSec,
      peak: result.peak,
      previewUrl: null,
      uploadStatus: 'pending',
      uploadProgress: 0,
    }
    pushTake(localTake)

    // Upload phase.
    setPhase('upload')
    try {
      const presigned = await callbacks.presign({
        cueId: inFlight.cueId,
        takeNumber: inFlight.takeNumber,
        versionNumber: inFlight.versionNumber,
        durationSec: result.durationSec,
      })
      updateTakeUpload(localTake.takeId, { uploadStatus: 'uploading' })
      await callbacks.upload({
        takeId: presigned.takeId,
        uploadUrl: presigned.uploadUrl,
        pcm: result.pcm,
      })
      updateTakeUpload(localTake.takeId, { uploadStatus: 'uploaded', uploadProgress: 1 })
      setPhase('qc_server')
      await callbacks.finalize({ takeId: presigned.takeId, cueId: inFlight.cueId })
      setPhase('review')
    } catch (err) {
      updateTakeUpload(localTake.takeId, { uploadStatus: 'failed' })
      setError(formatError(err))
      setPhase('review') // still surface review so actor can retry/pickup
    } finally {
      inFlightRef.current = null
    }
  }, [callbacks, pushTake, recorder, setError, setPhase, updateTakeUpload])

  const beginRecord = useCallback(async () => {
    if (!activeCue) return
    if (phase !== 'idle' && phase !== 'review' && phase !== 'advance') return
    const { takeNumber, versionNumber } = nextTakeNumber(takesByCue[activeCue.cueId])
    // Gain reset reminder if previous take's category differs significantly.
    if (previousCategory && previousCategory !== activeCue.category) {
      const reminder = copy.reset.resetReminder(previousCategory, activeCue.category)
      if (reminder) setResetReminder({ zh: reminder.zh, en: reminder.en })
    }
    await arm(activeCue, takeNumber, versionNumber)
  }, [activeCue, phase, takesByCue, previousCategory, arm, setResetReminder])

  const pickup = useCallback(async () => {
    if (!activeCue) return
    if (phase === 'recording' || phase === 'three_pop') return
    const { takeNumber, versionNumber } = nextPickupVersion(takesByCue[activeCue.cueId])
    await arm(activeCue, takeNumber, versionNumber)
  }, [activeCue, phase, takesByCue, arm])

  const advance = useCallback(() => {
    if (phase !== 'review' && phase !== 'idle') return
    setResetReminder(null)
    setPhase('advance')
    onAdvance()
    // onAdvance changes activeCue → next render's effect resets phase to idle.
  }, [phase, onAdvance, setPhase, setResetReminder])

  const abort = useCallback(() => {
    const inFlight = inFlightRef.current
    if (inFlight?.threePopTimer) clearTimeout(inFlight.threePopTimer)
    if (inFlight?.autoStopTimer) clearTimeout(inFlight.autoStopTimer)
    inFlightRef.current = null
    recorder.cancel()
    setPhase('idle')
  }, [recorder, setPhase])

  // After advance, drop back to idle once activeCue has changed.
  useEffect(() => {
    if (phase === 'advance') setPhase('idle')
  }, [activeCue?.cueId, phase, setPhase])

  // Cleanup on unmount.
  useEffect(() => {
    return () => {
      const inFlight = inFlightRef.current
      if (inFlight?.threePopTimer) clearTimeout(inFlight.threePopTimer)
      if (inFlight?.autoStopTimer) clearTimeout(inFlight.autoStopTimer)
      inFlightRef.current = null
    }
  }, [])

  return useMemo(
    () => ({ phase, beginRecord, pickup, advance, abort }),
    [phase, beginRecord, pickup, advance, abort],
  )
}

function makeClientTakeId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID()
  }
  return `take_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`
}

function formatError(e: unknown): string {
  if (e instanceof Error) return e.message
  if (typeof e === 'string') return e
  return 'Unknown error'
}
